// 8D Report PDF Generator
// Separated from exportUtils.ts to reduce complexity
import jsPDF from 'jspdf';

// ─── PDF Drawing Context ────────────────────────────────────────────────────
interface PdfCtx {
    pdf: jsPDF;
    pageWidth: number;
    margin: number;
    y: number;
}

function addTitle(ctx: PdfCtx, text: string): void {
    ctx.pdf.setFontSize(18);
    ctx.pdf.setFont('helvetica', 'bold');
    ctx.pdf.setTextColor(30, 64, 175);
    ctx.pdf.text(text, ctx.margin, ctx.y);
    ctx.y += 10;
}

function addSectionHeader(ctx: PdfCtx, step: string, title: string): void {
    if (ctx.y > 260) { ctx.pdf.addPage(); ctx.y = ctx.margin; }
    ctx.pdf.setFontSize(12);
    ctx.pdf.setFont('helvetica', 'bold');
    ctx.pdf.setFillColor(243, 244, 246);
    ctx.pdf.rect(ctx.margin, ctx.y - 4, ctx.pageWidth - ctx.margin * 2, 8, 'F');
    ctx.pdf.setTextColor(0, 0, 0);
    ctx.pdf.text(`${step}: ${title}`, ctx.margin + 2, ctx.y);
    ctx.y += 10;
}

function addText(ctx: PdfCtx, text: string, indent = 0): void {
    if (ctx.y > 270) { ctx.pdf.addPage(); ctx.y = ctx.margin; }
    ctx.pdf.setFontSize(10);
    ctx.pdf.setFont('helvetica', 'normal');
    ctx.pdf.setTextColor(0, 0, 0);
    const maxWidth = ctx.pageWidth - ctx.margin * 2 - indent;
    const lines = ctx.pdf.splitTextToSize(text, maxWidth);
    ctx.pdf.text(lines, ctx.margin + indent, ctx.y);
    ctx.y += lines.length * 5 + 3;
}

function addBulletPoint(ctx: PdfCtx, text: string, status?: string): void {
    if (ctx.y > 270) { ctx.pdf.addPage(); ctx.y = ctx.margin; }
    ctx.pdf.setFontSize(10);
    ctx.pdf.setFont('helvetica', 'normal');
    ctx.pdf.setFillColor(30, 64, 175);
    ctx.pdf.circle(ctx.margin + 2, ctx.y - 1.5, 1.5, 'F');
    const maxWidth = ctx.pageWidth - ctx.margin * 2 - 10 - (status ? 30 : 0);
    const lines = ctx.pdf.splitTextToSize(text, maxWidth);
    ctx.pdf.text(lines, ctx.margin + 8, ctx.y);
    if (status) {
        const statusColor = status === 'Completed' ? [34, 197, 94] :
            status === 'In Progress' ? [234, 179, 8] : [156, 163, 175];
        ctx.pdf.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
        ctx.pdf.roundedRect(ctx.pageWidth - ctx.margin - 25, ctx.y - 4, 22, 5, 1, 1, 'F');
        ctx.pdf.setFontSize(7);
        ctx.pdf.setTextColor(255, 255, 255);
        ctx.pdf.text(status, ctx.pageWidth - ctx.margin - 23, ctx.y - 0.5);
        ctx.pdf.setTextColor(0, 0, 0);
    }
    ctx.y += lines.length * 5 + 2;
}

// ─── helpers per D-Step ────────────────────────────────────────────────────
type Report8D = Parameters<typeof generate8DReportPDF>[0];

function renderReportHeader(ctx: PdfCtx, report: Report8D): void {
    addTitle(ctx, '8D Problem Solving Report');
    ctx.pdf.setFontSize(12);
    ctx.pdf.setFont('helvetica', 'bold');
    ctx.pdf.text(report.eightDNumber, ctx.margin, ctx.y);
    ctx.y += 6;
    ctx.pdf.setFontSize(11);
    ctx.pdf.setFont('helvetica', 'normal');
    ctx.pdf.text(report.title, ctx.margin, ctx.y);
    ctx.y += 8;
    ctx.pdf.setFontSize(9);
    ctx.pdf.setTextColor(100, 100, 100);
    ctx.pdf.text(`Status: ${report.status}`, ctx.margin, ctx.y);
    ctx.pdf.text(`Created: ${report.createdAt.toLocaleDateString()}`, ctx.margin + 50, ctx.y);
    if (report.teamLead) ctx.pdf.text(`Team Lead: ${report.teamLead}`, ctx.margin + 100, ctx.y);
    ctx.y += 12;
    ctx.pdf.setTextColor(0, 0, 0);
}

function renderTextSection(ctx: PdfCtx, step: string, title: string, value?: string): void {
    if (!value) return;
    addSectionHeader(ctx, step, title);
    addText(ctx, value);
}

function renderD3Containment(ctx: PdfCtx, items?: Array<{ description: string; status: string }>): void {
    if (!items?.length) return;
    addSectionHeader(ctx, 'D3', 'Interim Containment Actions');
    items.forEach(a => addBulletPoint(ctx, a.description, a.status));
}

function renderD3RootCauses(ctx: PdfCtx, causes?: Array<{ description: string; category: string; verified: boolean }>): void {
    if (!causes?.length) return;
    addSectionHeader(ctx, 'D3/D4', 'Root Cause Analysis');
    causes.forEach(rc => addBulletPoint(ctx, `[${rc.category}] ${rc.description}`, rc.verified ? 'Verified' : 'Pending'));
}

function renderD4Corrective(ctx: PdfCtx, items?: Array<{ description: string; owner: string; status: string }>): void {
    if (!items?.length) return;
    addSectionHeader(ctx, 'D4', 'Permanent Corrective Actions');
    items.forEach(a => addBulletPoint(ctx, `${a.description} (Owner: ${a.owner})`, a.status));
}

function renderD6Preventive(ctx: PdfCtx, items?: Array<{ description: string; status: string }>): void {
    if (!items?.length) return;
    addSectionHeader(ctx, 'D6', 'Preventive Actions');
    items.forEach(a => addBulletPoint(ctx, a.description, a.status));
}

function render8DSections(ctx: PdfCtx, report: Report8D): void {
    renderD3Containment(ctx, report.d3_containment);
    renderD3RootCauses(ctx, report.d3_rootCauses);
    renderD4Corrective(ctx, report.d4_corrective);
    renderD6Preventive(ctx, report.d6_preventive);
    renderTextSection(ctx, 'D0', 'Prepare and Plan', report.d0_problem);
    renderTextSection(ctx, 'D1', 'Team Formation', report.d1_team);
    renderTextSection(ctx, 'D2', 'Problem Description', report.d2_description);
    renderTextSection(ctx, 'D5', 'Verification and Validation', report.d5_validation);
    renderTextSection(ctx, 'D7', 'Team Recognition', report.d7_recognition);
    renderTextSection(ctx, 'D8', 'Closure', report.d8_closure);
}

function addPageFooters(ctx: PdfCtx): void {
    const pageCount = ctx.pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        ctx.pdf.setPage(i);
        ctx.pdf.setFontSize(8);
        ctx.pdf.setTextColor(150, 150, 150);
        ctx.pdf.text(
            `Magnus Resolve - 8D Report | Page ${i} of ${pageCount}`,
            ctx.pageWidth / 2, 285, { align: 'center' }
        );
    }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Generate a full 8D report PDF
 */
export const generate8DReportPDF = async (
    report: {
        eightDNumber: string;
        title: string;
        status: string;
        createdAt: Date;
        teamLead?: string;
        d0_problem?: string;
        d1_team?: string;
        d2_description?: string;
        d3_containment?: Array<{ description: string; status: string }>;
        d3_rootCauses?: Array<{ description: string; category: string; verified: boolean }>;
        d4_corrective?: Array<{ description: string; owner: string; status: string }>;
        d5_validation?: string;
        d6_preventive?: Array<{ description: string; status: string }>;
        d7_recognition?: string;
        d8_closure?: string;
    }
): Promise<void> => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const ctx: PdfCtx = { pdf, pageWidth: pdf.internal.pageSize.getWidth(), margin: 15, y: 15 };

    renderReportHeader(ctx, report);
    render8DSections(ctx, report);
    addPageFooters(ctx);

    pdf.save(`8D_Report_${report.eightDNumber}.pdf`);
};
